import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentListColumn from "work/work-agreements/contracts/_views/document-list-column";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked, MaskedTeam } from "work/masked-fields";
import { useSession } from "users/__session/session-context";
import WorkAgreement from "work/work-agreements/work-agreement";
import { replacePolicyPath } from "work/policy-replacements/policy-replacement-routes";
import { workPaneNarrowerThan, workPaneProseInset } from "work/_views/work-pane-layout";

/**
 * No left margin. The list's heading and rows carry Material's 16px inset of their own, which is the
 * prose inset already, so a margin here stood the heading 40px right of the Leave Team button above
 * it. The helper text gets the inset it has no list to give it.
 */
const ListContainer = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  height: "100%",
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  overflowY: "auto",
}));
const HelperText = styled(Typography)(({ theme }) => ({
  ...workPaneProseInset(theme),
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  workAgreement: WorkAgreement;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { workAgreement } = props;
  const session = useSession();

  /**
   * Whether to offer replacing a client policy across every agreement that holds it (#6257): to the
   * client team leader, as the organization they act for, on running work. The server decides
   * again on each agreement; this only decides whether to offer the action at all.
   */
  const canReplaceClientPolicies =
    workAgreement.isActiveWork &&
    MaskedTeam.isPresentNotMasked(workAgreement.clientTeam) &&
    Boolean(
      workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id) &&
        workAgreement.clientTeam.leader.legalEntityId.isEqualTo(session.user?.legalEntityId),
    );
  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const policyDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(documentDownloadFailureMessage(err, "Failed to download the document. Please try again later."), {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (Masked.isMasked(workAgreement.clientPolicies) || Masked.isMasked(workAgreement.vendorPolicies)) {
    return "You do not have permission to view contracts.";
  }

  const policyGroups = [
    {
      documents: workAgreement.clientPolicies,
      documentType: WorkDocumentType.ClientPolicy,
    },
    {
      documents: workAgreement.vendorPolicies,
      documentType: WorkDocumentType.VendorPolicy,
    },
  ];
  const policyDocumentsCount = policyGroups.reduce((count, group) => count + group.documents.length, 0);

  return (
    <ListContainer container direction="row">
      <DocumentListColumn>
        {policyDocumentsCount === 0 && (
          <HelperText>No policy documents</HelperText>
        )}
        {policyGroups.map((group) => (
          group.documents.length > 0 && (
            <DocumentList
              key={group.documentType}
              documents={group.documents}
              documentType={group.documentType}
              downloadingFileId={isDownloadingFile}
              replaceLinkFor={
                canReplaceClientPolicies && group.documentType === WorkDocumentType.ClientPolicy
                  ? (document) => replacePolicyPath(document.id.value, workAgreement.id?.value)
                  : undefined
              }
              onDownload={handleDownloadDocumentById}
            />
          )
        ))}
      </DocumentListColumn>
    </ListContainer>
  );
}
